/**
 * Transacciones de un turno: clasificación, búsqueda y productividad por lado.
 *
 * Son las mismas reglas del diálogo del panel anterior
 * (`src/liquidaciones/index.js`: `obtenerTipoDocumentoPorSerie` y
 * `calcularProductividadLadoProducto`), que a su vez son los datos del
 * controlador (`turno.transactions`, del JSON del surtidor).
 *
 * El módulo es **puro** (sin React) para poder probarlo y para que el diálogo
 * sólo se ocupe de pintar.
 */

import type { TransaccionTurno } from '../reportes.api';

/** Tipos de documento del selector (mismo orden del panel anterior). */
export const TIPOS_TRANSACCION = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'FACTURA', etiqueta: 'Factura' },
  { valor: 'BOLETA', etiqueta: 'Boleta' },
  { valor: 'NOTA_VENTA', etiqueta: 'Nota de Venta' },
  { valor: 'CONSUMO_INTERNO', etiqueta: 'Consumo Interno' },
  { valor: 'CALIBRACION', etiqueta: 'Calibraciones' },
  { valor: 'CREDITO', etiqueta: 'Créditos' },
  { valor: 'PAGO_ADELANTADO', etiqueta: 'Pago Adelantado' },
  { valor: 'CON_DESCUENTO', etiqueta: 'Solo con descuento' },
] as const;

/** Casa del tipo de documento: el prefijo de la serie y la marca de adelanto. */
const SIGLAS: Record<string, string> = {
  F: 'FACTURA',
  B: 'BOLETA',
  N: 'NOTA_VENTA',
  I: 'CONSUMO_INTERNO',
  L: 'CALIBRACION',
  C: 'CREDITO',
};

/** Número de la transacción, venga como texto o como número. */
function numero(valor: number | string | null | undefined): number {
  const cifra = Number(valor ?? 0);

  return Number.isFinite(cifra) ? cifra : 0;
}

/** Tipo de documento de una transacción (el `tipo` del filtro). */
export function tipoDeTransaccion(transaccion: TransaccionTurno): string {
  // El pago adelantado se marca en la propia transacción, no en la serie.
  if (transaccion.isAdvancePayment === true) return 'PAGO_ADELANTADO';

  const sigla = String(transaccion.serieTransaction ?? '')
    .trim()
    .charAt(0)
    .toUpperCase();

  return SIGLAS[sigla] ?? '';
}

/** Comprobante de la transacción (`BE01-39872`). */
export function documentoDeTransaccion(transaccion: TransaccionTurno): string {
  const serie = String(transaccion.serieTransaction ?? '').trim();
  const numeroDocumento = numero(transaccion.invoicedTransaction);

  if (!serie) return '—';
  if (numeroDocumento === 0) return serie;

  return `${serie}-${numeroDocumento}`;
}

/** Fecha y hora de la transacción (el controlador manda una sola forma). */
export function fechaDeTransaccion(transaccion: TransaccionTurno): string {
  return (
    transaccion.dateTimeTransaction ??
    transaccion.transactionDate ??
    transaccion.dateTransaction ??
    ''
  );
}

/** Texto sobre el que busca el buscador del diálogo. */
function textoBuscable(transaccion: TransaccionTurno): string {
  return [
    transaccion.idTransaction,
    transaccion.keyTransaction,
    documentoDeTransaccion(transaccion),
    transaccion.serieTransaction,
    transaccion.FuelGradeName,
    transaccion.pumpTransaction,
    transaccion.nozzleTransaction,
    transaccion.priceTransaction,
    transaccion.volumeTransaction,
    transaccion.amountTransaction,
    transaccion.discountTransaction,
    transaccion.totalVolumeTransaction,
    fechaDeTransaccion(transaccion),
  ]
    .map((valor) => String(valor ?? ''))
    .join(' ')
    .toLowerCase();
}

export interface FiltrosTransacciones {
  /** Texto libre: transacción, documento, producto… */
  busqueda: string;
  /** Valor de `TIPOS_TRANSACCION` (vacío = todos). */
  tipo: string;
}

/** Transacciones que cumplen el buscador y el tipo elegido. */
export function filtrarTransacciones(
  transacciones: TransaccionTurno[],
  filtros: FiltrosTransacciones,
): TransaccionTurno[] {
  const busqueda = filtros.busqueda.trim().toLowerCase();

  return transacciones.filter((transaccion) => {
    if (busqueda && !textoBuscable(transaccion).includes(busqueda)) return false;

    if (!filtros.tipo) return true;

    // «Sólo con descuento» es un atajo: no mira el tipo de documento.
    if (filtros.tipo === 'CON_DESCUENTO') return numero(transaccion.discountTransaction) !== 0;

    return tipoDeTransaccion(transaccion) === filtros.tipo;
  });
}

/** Productividad de una lado y un producto. */
export interface ProductividadLado {
  lado: string;
  producto: string;
  transacciones: number;
  /** Contómetro al empezar (`totalVolume` de la primera − su volumen). */
  contometro_inicial: number;
  /** Contómetro al terminar (`totalVolume` de la última). */
  contometro_final: number;
  volumen: number;
  monto: number;
  descuento: number;
}

/** Totales de la productividad (la fila «TOTALES» del panel anterior). */
export interface TotalesProductividad {
  transacciones: number;
  volumen: number;
  monto: number;
  descuento: number;
}

/**
 * Productividad por lado y producto del turno.
 *
 * Igual que el panel anterior: se agrupa por `lado|producto`, los importes y el
 * volumen se suman, y los contómetros salen de la **primera y la última**
 * transacción del grupo ordenadas por fecha y hora (y, si empatan, por número de
 * transacción), no del orden en que llegan.
 */
export function productividadPorLado(transacciones: TransaccionTurno[]): {
  filas: ProductividadLado[];
  totales: TotalesProductividad;
} {
  const grupos = new Map<
    string,
    ProductividadLado & { movimientos: { fecha: string; id: number; volumen: number; total: number }[] }
  >();

  for (const transaccion of transacciones) {
    const lado = String(transaccion.pumpTransaction ?? 'N/A');
    const producto = String(transaccion.FuelGradeName ?? '').trim() || 'N/A';
    const clave = `${lado}|${producto}`;
    const volumen = numero(transaccion.volumeTransaction);
    const total = numero(transaccion.totalVolumeTransaction);

    let grupo = grupos.get(clave);

    if (!grupo) {
      grupo = {
        lado,
        producto,
        transacciones: 0,
        contometro_inicial: 0,
        contometro_final: 0,
        volumen: 0,
        monto: 0,
        descuento: 0,
        movimientos: [],
      };

      grupos.set(clave, grupo);
    }

    grupo.transacciones += 1;
    grupo.volumen += volumen;
    grupo.monto += numero(transaccion.amountTransaction);
    grupo.descuento += numero(transaccion.discountTransaction);
    grupo.movimientos.push({
      fecha: fechaDeTransaccion(transaccion),
      id: numero(transaccion.idTransaction),
      volumen,
      total,
    });
  }

  const filas: ProductividadLado[] = [];

  for (const grupo of grupos.values()) {
    grupo.movimientos.sort((a, b) => {
      const fechaA = Date.parse(a.fecha);
      const fechaB = Date.parse(b.fecha);

      if (Number.isFinite(fechaA) && Number.isFinite(fechaB) && fechaA !== fechaB) {
        return fechaA - fechaB;
      }

      if (Number.isFinite(fechaA) && !Number.isFinite(fechaB)) return -1;
      if (!Number.isFinite(fechaA) && Number.isFinite(fechaB)) return 1;

      return a.id - b.id;
    });

    const primera = grupo.movimientos[0];
    const ultima = grupo.movimientos[grupo.movimientos.length - 1];

    filas.push({
      lado: grupo.lado,
      producto: grupo.producto,
      transacciones: grupo.transacciones,
      contometro_inicial: primera ? primera.total - primera.volumen : 0,
      contometro_final: ultima ? ultima.total : 0,
      volumen: grupo.volumen,
      monto: grupo.monto,
      descuento: grupo.descuento,
    });
  }

  // Primero la lado (numérica si se puede) y, dentro, el producto.
  filas.sort((a, b) => {
    const ladoA = Number(a.lado);
    const ladoB = Number(b.lado);
    const ambasNumericas = Number.isFinite(ladoA) && Number.isFinite(ladoB);

    if (ambasNumericas && ladoA !== ladoB) return ladoA - ladoB;
    if (!ambasNumericas && a.lado !== b.lado) return a.lado.localeCompare(b.lado);

    return a.producto.localeCompare(b.producto);
  });

  const totales = transacciones.reduce<TotalesProductividad>(
    (suma, transaccion) => ({
      transacciones: suma.transacciones + 1,
      volumen: suma.volumen + numero(transaccion.volumeTransaction),
      monto: suma.monto + numero(transaccion.amountTransaction),
      descuento: suma.descuento + numero(transaccion.discountTransaction),
    }),
    { transacciones: 0, volumen: 0, monto: 0, descuento: 0 },
  );

  return { filas, totales };
}
